import React, { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Reply } from "@material-ui/icons";
import path from "path";
import { useTheme } from "@material-ui/core/styles";

// Componentes
import QuotedMessage from "./QuotedMessage";
import FloatingMessageActions from "../../FloatingMessageActions";
import SelectMessageCheckbox from "../SelectMessageCheckbox";
import { ForwardMessageContext } from "../../../context/ForwarMessage/ForwardMessageContext";
import YouTubePreview from "../../ModalYoutubeCors";
import MarkdownWrapper from "../../MarkdownWrapper";
import { formatMessageTime } from "../../../utils/dateFormatters";

// Utils
import { isYouTubeLink } from "../utils/messageFormatters";
import { useReactionMicrointeractions } from "../hooks/useReactionMicrointeractions";
import { useReactionStyles } from "../styles/reactionStyles";

const FILE_PREFIX_REGEX = /^(\d{10,})([_-])(.*)$/;

const normalizeAttachmentLabel = (value) => {
  if (!value) return "";
  const raw = `${value}`.trim();
  if (!raw) return "";
  const match = raw.match(FILE_PREFIX_REGEX);
  const candidate = match && match[3] ? match[3] : raw;
  return candidate.replace(/_/g, " ").replace(/\s+/g, " ").trim();
};

const MessageBubbleLeft = ({
  message,
  index,
  classes,
  isGroup,
  hoveredMessageId,
  whatsappId,
  queueId,
  showSelectMessageCheckbox,
  lgpdDeleteMessage,
  xmlRegex,
  reactionList,
  getMessageSpacing,
  handleDoubleClickMessage,
  handleMessageHover,
  handleMessageLeave,
  handleFloatingReply,
  handleFloatingForward,
  handleFloatingReact,
  handleMenuMouseEnter,
  handleMenuMouseLeave,
  handleOpenReactionDetails,
  renderMedia,
  renderAudioDirect,
  renderContactDirect,
}) => {
  const theme = useTheme();
  const { handleReactionClick, getReactionAnimationClasses, cleanup } = useReactionMicrointeractions();
  const reactionClasses = useReactionStyles();

  const [previewExpanded, setPreviewExpanded] = useState(false);
  const previewTextRef = useRef(null);
  const [previewCanExpand, setPreviewCanExpand] = useState(false);
  const textRef = useRef(null);
  const [textExpanded, setTextExpanded] = useState(false);
  const [textCanExpand, setTextCanExpand] = useState(false);

  useEffect(() => cleanup, [cleanup]);

  const adPreview = useMemo(() => {
    try {
      if (!message?.dataJson) return null;
      const dj = JSON.parse(message.dataJson);
      const ext = dj?.message?.extendedTextMessage || dj?.message?.ephemeralMessage?.message?.extendedTextMessage;
      if (!ext) return null;
      const ad = ext?.contextInfo?.externalAdReply;
      const url = ext?.canonicalUrl || ext?.matchedText || ad?.sourceUrl;
      const title = ad?.title || null;
      const body = ad?.body || null;
      const toBase64 = (arr) => {
        try {
          const bytes = Array.isArray(arr) ? new Uint8Array(arr) : arr?.data ? new Uint8Array(arr.data) : null;
          if (!bytes) return null;
          const chunkSize = 0x8000;
          let binary = "";
          for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.subarray(i, i + chunkSize);
            binary += String.fromCharCode.apply(null, chunk);
          }
          return window.btoa(binary);
        } catch { return null; }
      };
      let thumbSrc = ad?.thumbnailUrl || null;
      if (!thumbSrc) {
        const jpeg = ad?.jpegThumbnail || ext?.jpegThumbnail;
        if (jpeg) {
          if (typeof jpeg === 'string') {
            thumbSrc = jpeg.startsWith('http') || jpeg.startsWith('data:') ? jpeg : `data:image/jpeg;base64,${jpeg}`;
          } else {
            const b64 = toBase64(jpeg);
            if (b64) thumbSrc = `data:image/jpeg;base64,${b64}`;
          }
        }
      }
      const originalText = ext?.text || null;
      if (!url) return null;
      return { url, title, body, thumbSrc, originalText };
    } catch { return null; }
  }, [message?.dataJson]);

  // Detectar overflow do bloco de preview para decidir exibir "Ver mais"
  useEffect(() => {
    const checkOverflow = () => {
      if (!previewTextRef.current || !adPreview) {
        setPreviewCanExpand(false);
        return;
      }
      const el = previewTextRef.current;
      const canExpand = el.scrollHeight - 1 > el.clientHeight;
      setPreviewCanExpand(canExpand);
    };

    const rId = requestAnimationFrame(checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => {
      cancelAnimationFrame(rId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [adPreview, previewExpanded]);

  // Detecta overflow no corpo do texto (15 linhas)
  useEffect(() => {
    const checkOverflow = () => {
      if (!textRef.current) {
        setTextCanExpand(false);
        return;
      }
      const el = textRef.current;
      const canExpand = el.scrollHeight - 1 > el.clientHeight;
      setTextCanExpand(canExpand);
    };
    const rId = requestAnimationFrame(checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => {
      cancelAnimationFrame(rId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [message?.body, textExpanded]);

  // Evita bloco de texto vazio para áudios
  const shouldShowTextBlock = useMemo(() => {
    const mediaNameDiffersBody =
      (message.mediaType === "image" || message.mediaType === "video") &&
      path.basename(message.mediaUrl || "").trim() !== (message.body || "").trim();
    const nonAudioTextCase =
      message.mediaType !== "audio" &&
      message.mediaType !== "reactionMessage" &&
      message.mediaType !== "locationMessage" &&
      message.mediaType !== "contactMessage";
    const reactionSpecial = !!message.quotedMsg && message.mediaType === "reactionMessage";
    const hasOtherBlocks = !!adPreview || !!message.quotedMsg || mediaNameDiffersBody || nonAudioTextCase || reactionSpecial;
    if (message.mediaType === "audio") {
      return hasOtherBlocks;
    }
    return true;
  }, [message, adPreview]);

  const isAudio = message.mediaType === "audio";
  const { showSelectMessageCheckbox: selectionMode, selectedMessages, toggleSelectedMessage } = React.useContext(ForwardMessageContext);
  const isSelected = React.useMemo(() => selectedMessages?.some(m => m.id === message.id), [selectedMessages, message?.id]);

  return (
    <div
      className={clsx(isAudio ? classes.messageLeftBare : classes.messageLeft, {
        [classes.messageSelected]: selectionMode && isSelected,
        [classes.messageSelectable]: selectionMode,
      })}
      title={message.queueId && message.queue?.name}
      onDoubleClick={() => handleDoubleClickMessage(message)}
      onMouseEnter={() => handleMessageHover(message.id)}
      onMouseLeave={handleMessageLeave}
      onClick={(e) => {
        if (selectionMode) {
          e.preventDefault();
          e.stopPropagation();
          toggleSelectedMessage(message);
        }
      }}
      style={getMessageSpacing(message, index)}
    >
      {hoveredMessageId === message.id && (
        <div className={classes.floatingActionsLeft}>
          <FloatingMessageActions
            message={message}
            isVisible={hoveredMessageId === message.id}
            fromMe={false}
            isGroup={isGroup}
            whatsappId={whatsappId}
            queueId={queueId}
            onReply={handleFloatingReply}
            onForward={handleFloatingForward}
            onReact={handleFloatingReact}
            onMouseEnter={handleMenuMouseEnter}
            onMouseLeave={handleMenuMouseLeave}
          />
        </div>
      )}

      {showSelectMessageCheckbox && (
        <SelectMessageCheckbox message={message} />
      )}

      {message.isForwarded && (
        <div className={classes.forwardedIndicator}>
          <span className={classes.forwardMessage}>
            <Reply style={{ color: "grey", transform: "scaleX(-1)" }} />
            Encaminhada
          </span>
        </div>
      )}

      {isGroup && (
        <span className={classes.messageContactName}>
          {message.contact?.name}
        </span>
      )}

      {isYouTubeLink(message.body) && (
        <div className={classes.youtubePreviewContainer}>
          <YouTubePreview videoUrl={message.body} />
        </div>
      )}

      {!lgpdDeleteMessage && message.isDeleted && (
        <div className={classes.deletedMessageIndicator}>
          <span className={classes.deletedMessage}>
            🚫 Essa mensagem foi apagada pelo contato
          </span>
        </div>
      )}

      {(message.mediaUrl ||
        message.mediaType === "locationMessage" ||
        message.mediaType === "contactMessage") && (
        <div className={classes.mediaContainer}>
          {message.mediaType === 'audio' ? renderAudioDirect(message, index) : 
           message.mediaType === 'contactMessage' ? renderContactDirect(message, whatsappId, queueId) :
           renderMedia(message)}
        </div>
      )}

      {shouldShowTextBlock && (
      <div
        className={clsx(classes.textContentItem, {
          [classes.textContentItemDeleted]: message.isDeleted,
        })}
      >
        <QuotedMessage message={message} classes={classes} />

        {adPreview && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Abrir link do preview"
            onClick={() => window.open(adPreview.url, '_blank', 'noopener,noreferrer')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                window.open(adPreview.url, '_blank', 'noopener,noreferrer');
              }
            }}
            style={{
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.action.hover,
              borderRadius: 8,
              padding: 8,
              marginBottom: 6,
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              {adPreview.thumbSrc && (
                <img
                  src={adPreview.thumbSrc}
                  alt={adPreview.title || 'preview'}
                  style={{ width: 64, height: 64, borderRadius: 6, objectFit: 'cover', flex: '0 0 auto' }}
                  loading="lazy"
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  ref={previewTextRef}
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: previewExpanded ? 'unset' : 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    whiteSpace: 'pre-wrap',
                    fontWeight: adPreview.title ? 500 : 400
                  }}
                >
                  {[
                    adPreview.title,
                    adPreview.body
                  ].filter(Boolean).join(' — ')}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <a
                href={adPreview.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: theme.palette.primary.main, textDecoration: 'none', wordBreak: 'break-all', maxWidth: '75%' }}
              >
                {adPreview.url}
              </a>
              {previewCanExpand && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setPreviewExpanded(v => !v); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setPreviewExpanded(v => !v); }
                  }}
                  style={{ cursor: 'pointer', color: theme.palette.primary.main, fontWeight: 500, userSelect: 'none' }}
                >
                  {previewExpanded ? 'Ver menos' : 'Ver mais'}
                </span>
              )}
            </div>
          </div>
        )}

        {((message.mediaUrl !== null &&
          (message.mediaType === "image" || message.mediaType === "video") &&
          path.basename(message.mediaUrl).trim() !== message.body.trim()) ||
          (message.mediaType !== "audio" &&
            message.mediaType !== "image" &&
            message.mediaType !== "video" &&
            message.mediaType !== "reactionMessage" &&
            message.mediaType !== "locationMessage" &&
            message.mediaType !== "contactMessage")) && (
          <div className={classes.messageBodyContainer}>
            {(() => {
              const displayText = adPreview?.originalText ?? message.body;
              const isDeletedMessage = lgpdDeleteMessage && message.isDeleted;
              const hasMediaUrl = Boolean(message?.mediaUrl);
              const mediaType = (message?.mediaType || "").toLowerCase();
              const isVideoMedia = mediaType === "video";
              const isDocumentMedia = hasMediaUrl && ![
                "audio",
                "image",
                "video",
                "reactionmessage",
                "locationmessage",
                "contactmessage"
              ].includes(mediaType);
              const normalizedFileName = normalizeAttachmentLabel(
                message.filename || message.originalName || ""
              );
              const normalizedBodyLabel = normalizeAttachmentLabel(displayText);
              const hasDocumentCaption = isDocumentMedia && Boolean(displayText?.trim()) && (
                !normalizedFileName ||
                normalizedBodyLabel.toLowerCase() !== normalizedFileName.toLowerCase()
              );
              const isNormalizedBodySameAsRaw = normalizeAttachmentLabel(message.body) === normalizedBodyLabel;

              if (isVideoMedia && hasMediaUrl) {
                return null;
              }

              if (isDocumentMedia && (!hasDocumentCaption || isNormalizedBodySameAsRaw)) {
                return null;
              }

              const effectiveText = isDeletedMessage
                ? "🚫 _Mensagem apagada_"
                : displayText;

              return (
                <div>
                  <div
                    ref={textRef}
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: textExpanded ? 'unset' : 15,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    <MarkdownWrapper>{effectiveText}</MarkdownWrapper>
                  </div>
                  {textCanExpand && (
                    <span
                      onClick={() => setTextExpanded(v => !v)}
                      style={{ cursor: 'pointer', color: theme.palette.primary.main, fontWeight: 500, userSelect: 'none' }}
                    >
                      {textExpanded ? 'Ver menos' : 'Ver mais'}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {message.quotedMsg && message.mediaType === "reactionMessage" && (
          <div className={classes.reactionMessageContainer}>
            <span className={classes.reactionMessageText}>
              <MarkdownWrapper>
                {message?.contact?.name} reagiu... {message.body}
              </MarkdownWrapper>
            </span>
          </div>
        )}

        {message.mediaType !== 'audio' && (
          <span className={classes.timestamp}>
            {message.isEdited && <span style={{ marginRight: 8 }}>Editada</span>}
            {formatMessageTime(message.createdAt)}
          </span>
        )}
      </div>
      )}

      {reactionList?.length > 0 && (
        <div className={reactionClasses.reactionAreaLeft}>
          {reactionList.map((item, index) => {
            const userReacted = message.reactionsMsg?.some(
              (reaction) => reaction.body === item.body && reaction.fromMe
            );
            const onReactionClick = (emoji) => {
              handleReactionClick(
                message.id,
                emoji,
                (messageId, reactionEmoji) => handleFloatingReact(message, reactionEmoji)
              );
            };
            const animationClasses = getReactionAnimationClasses(message.id, item.body);
            return (
              <span
                key={index}
                className={clsx(
                  reactionClasses.reactionMsg,
                  {
                    [reactionClasses.reactionMsgActive]: userReacted,
                    'reaction-shimmer': userReacted,
                  },
                  animationClasses,
                  {
                    'reaction-bouncing': animationClasses.includes('reaction-bouncing'),
                  }
                )}
                onClick={() => onReactionClick(item.body)}
                title={
                  userReacted ? "Clique para remover sua reação" : "Clique para reagir"
                }
                style={{
                  userSelect: animationClasses ? 'none' : 'auto',
                  pointerEvents: animationClasses ? 'none' : 'auto',
                  zIndex: userReacted ? 15 : 10,
                  ...(userReacted && { transform: 'translateZ(2px)' }),
                }}
              >
                {item.body}
                {item.quantidade > 1 && (
                  <span className={reactionClasses.reactionCounter}>
                    {item.quantidade}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MessageBubbleLeft;
